/**
 * Залишки малоцінки (МШП/МНМА) за період (2026-08-27; 2026-08-31 — Норма #29:
 * InfiniteDocGrid із серверною пагінацією/сортуванням, кнопка «сформувати»,
 * період стартує з «періоду за замовчуванням» шапки; друк — повний набір).
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { orgs } from '@/api/catalogs';
import { departmentsApi } from '@/api/hr';
import { assetsRegistry, ASSET_STATUS_UA, type LowValueBalanceRow } from '@/api/assetsRegistry';
import DatePopover from '@/components/DatePopover';
import InfiniteDocGrid from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useDefaultPeriod, useDefaults } from '@/store/defaults';
import TchTable from '@/shared/components/TchTable';

const fmt2 = (v: string | number | null | undefined) => v == null || v === '' || Number(v) === 0 ? ''
  : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (v: string | null | undefined) => v ? new Date(v).toLocaleDateString('uk-UA') : '';
const QTY = ['opening_qty', 'in_qty', 'out_qty', 'closing_qty'] as const;
const COST = ['opening_cost', 'in_cost', 'out_cost', 'closing_cost'] as const;
const QTY_HEAD = ['Поч., шт', 'Надійшло, шт', 'Вибуло, шт', 'Кін., шт'];
const COST_HEAD = ['Поч., грн', 'Надійшло, грн', 'Вибуло, грн', 'Кін., грн'];

export default function LowValueBalancesPage() {
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  // Канон DocListFilters: локальні дати > глобальний період шапки (порожньо).
  const gPeriod = useDefaultPeriod();
  const y = new Date().getFullYear();
  const effFrom = (v: string) => v || (gPeriod.dateFrom ? gPeriod.dateFrom.slice(0, 10) : `${y}-01-01`);
  const effTo = (v: string) => v || (gPeriod.dateTo ? gPeriod.dateTo.slice(0, 10) : new Date().toISOString().slice(0, 10));
  const [orgId, setOrgId] = useState(defaultOrgId || '');
  const [deptId, setDeptId] = useState('');
  const [dFrom, setDFrom] = useState('');
  const [dTo, setDTo] = useState('');
  const [applied, setApplied] = useState<null | { orgId: string; deptId: string; dFrom: string; dTo: string }>(null);
  const [totals, setTotals] = useState<Record<string, string>>({});
  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const depsQ = useQuery({
    queryKey: ['departments-items', orgId],
    queryFn: () => departmentsApi.list({ organization_id: orgId, limit: 500 }).then((r) => r.items),
    enabled: !!orgId,
  });
  const { datasource, total } = useInfiniteGridDatasource<LowValueBalanceRow>(
    async (limit, offset, sort) => {
      if (!applied) return { items: [], total: 0 };
      const d = await assetsRegistry.lowValueBalances({
        organization_id: applied.orgId, date_from: applied.dFrom, date_to: applied.dTo,
        department_id: applied.deptId || undefined,
        sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
      });
      setTotals(d.totals);
      return { items: d.rows, total: d.total_rows };
    },
    [applied],
  );
  const cols = useMemo<ColDef<LowValueBalanceRow>[]>(() => [
    { field: 'inventory_number', headerName: 'Інв. №', width: 110, sortable: true },
    { field: 'name', headerName: 'Назва', flex: 1, minWidth: 220, sortable: true },
    { field: 'department_name', headerName: 'Підрозділ', width: 150, sortable: true },
    { field: 'responsible_name', headerName: 'МВО', width: 150, sortable: true },
    {
      field: 'status', headerName: 'Стан', width: 120, sortable: false,
      valueFormatter: (p) => ASSET_STATUS_UA[p.value as string] ?? p.value,
    },
    { field: 'commissioning_date', headerName: 'Введено', width: 110, sortable: true, valueFormatter: (p) => fmtD(p.value) },
    { field: 'retirement_date', headerName: 'Вибуло', width: 110, sortable: true, valueFormatter: (p) => fmtD(p.value) },
    ...QTY.map((f, i) => ({
      field: f, headerName: QTY_HEAD[i], width: 110, sortable: true,
      cellStyle: { textAlign: 'right' as const },
      valueFormatter: (p: { value?: number }) => p.value ? String(p.value) : '',
    })),
    ...COST.map((f, i) => ({
      field: f, headerName: COST_HEAD[i], width: 120, sortable: true,
      cellStyle: { textAlign: 'right' as const },
      valueFormatter: (p: { value?: string }) => fmt2(p.value),
    })),
  ], []);
  const [printRows, setPrintRows] = useState<LowValueBalanceRow[] | null>(null);
  const doPrint = async () => {
    if (!applied) return;
    const d = await assetsRegistry.lowValueBalances({
      organization_id: applied.orgId, date_from: applied.dFrom, date_to: applied.dTo,
      department_id: applied.deptId || undefined,
    });
    setPrintRows(d.rows);
    setTimeout(() => { window.print(); setPrintRows(null); }, 50);
  };
  const orgName = orgList.data?.items.find((o) => o.id === (applied?.orgId ?? orgId))?.name ?? '';

  return (
    <div className="card">
      <div className="toolbar print-hide" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Залишки малоцінки в експлуатації</h2>
        <button className="primary" style={{ marginLeft: 'auto' }} disabled={!applied || (total ?? 0) === 0} onClick={doPrint}>друкувати</button>
      </div>
      <div className="print-hide" style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
        <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setDeptId(''); }} style={{ width: 220 }}>
          <option value="">— організація —</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
        <select value={deptId} onChange={(e) => setDeptId(e.target.value)} style={{ width: 190 }}>
          <option value="">Усі підрозділи</option>
          {(depsQ.data ?? []).map((d: { id: string; name: string }) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          title="Порожньо — діє «період за замовчуванням» з шапки">
          <DatePopover value={dFrom} onChange={setDFrom} />
          <span style={{ color: 'var(--muted)' }}>—</span>
          <DatePopover value={dTo} onChange={setDTo} />
        </span>
        <button className="primary" disabled={!orgId}
          onClick={() => setApplied({ orgId, deptId, dFrom: effFrom(dFrom), dTo: effTo(dTo) })}>
          сформувати
        </button>
      </div>
      {!applied && <p className="print-hide" style={{ color: 'var(--muted)' }}>Задайте фільтри і натисніть «сформувати».</p>}
      {applied && (
        <div className="print-hide">
          <div style={{ marginBottom: 6, fontSize: '0.85rem' }}>
            <b>Залишки малоцінки</b>{orgName ? ` — ${orgName}` : ''} · період {fmtD(applied.dFrom)} — {fmtD(applied.dTo)} · рядків: {total ?? '…'}
          </div>
          <div style={{ height: 440 }}>
            <InfiniteDocGrid<LowValueBalanceRow>
              datasource={datasource}
              columnDefs={cols}
              storageKey="lv-balances"
            />
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: 6, fontSize: '0.82rem', fontWeight: 600 }}>
            <span>Разом:</span>
            {QTY.map((f, i) => <span key={f} style={{ color: 'var(--muted)' }}>{QTY_HEAD[i]}: <b style={{ color: 'inherit' }}>{Number(totals[f] ?? 0)}</b></span>)}
            {COST.map((f, i) => <span key={f} style={{ color: 'var(--muted)' }}>{COST_HEAD[i]}: <b style={{ color: 'inherit' }}>{fmt2(totals[f])}</b></span>)}
          </div>
        </div>
      )}
      {printRows && (
        <div className="print-page" style={{ fontSize: '0.78rem' }}>
          <div style={{ marginBottom: 6 }}>
            <b>Залишки малоцінки в експлуатації</b>{orgName ? ` — ${orgName}` : ''}<br />
            Період: {fmtD(applied?.dFrom)} — {fmtD(applied?.dTo)}
          </div>
          <TchTable style={{ minWidth: 1000 }}>
            <thead>
              <tr>
                <th>Інв. №</th><th>Назва</th><th>Підрозділ</th><th>МВО</th><th>Введено</th><th>Вибуло</th>
                {QTY_HEAD.map((h) => <th key={h} style={{ textAlign: 'right' }}>{h}</th>)}
                {COST_HEAD.map((h) => <th key={h} style={{ textAlign: 'right' }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {printRows.map((r) => (
                <tr key={r.id}>
                  <td>{r.inventory_number}</td><td>{r.name}</td>
                  <td>{r.department_name ?? ''}</td><td>{r.responsible_name ?? ''}</td>
                  <td>{fmtD(r.commissioning_date)}</td><td>{fmtD(r.retirement_date)}</td>
                  {QTY.map((f) => <td key={f} style={{ textAlign: 'right' }}>{r[f] || ''}</td>)}
                  {COST.map((f) => <td key={f} style={{ textAlign: 'right' }}>{fmt2(r[f])}</td>)}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700 }}>
                <td colSpan={6}>Разом</td>
                {QTY.map((f) => <td key={f} style={{ textAlign: 'right' }}>{Number(totals[f] ?? 0)}</td>)}
                {COST.map((f) => <td key={f} style={{ textAlign: 'right' }}>{fmt2(totals[f])}</td>)}
              </tr>
            </tfoot>
          </TchTable>
        </div>
      )}
    </div>
  );
}
